import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import {
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faFileText,
  faExclamationTriangle,
  faArrowLeft,
  faPlay
} from '@/fontawesome';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Job, JobExecution, JobType, JobStatus } from '@/models';

const AUTO_REFRESH_INTERVAL = 2000;

const pageSize = 50;

export default function AdminJobDetail() {
  const { jobType } = useParams<{ jobType: JobType }>();
  const [job, setJob] = useState<Job | null>(null);
  const [executions, setExecutions] = useState<JobExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [runningJob, setRunningJob] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const refreshIntervalRef = useRef<number | null>(null);

  // Check if any execution is running or if the job itself is running
  const hasRunningExecution = job?.running === true || executions.some(e => e.status === 'RUNNING');

  useEffect(() => {
    if (jobType) {
      setCurrentPage(1);
      fetchJobDetails();
    }
  }, [jobType]);

  useEffect(() => {
    if (jobType) {
      fetchJobDetails();
    }
  }, [currentPage]);

  // Auto-refresh while an execution is running
  useEffect(() => {
    if (hasRunningExecution) {
      refreshIntervalRef.current = window.setInterval(() => {
        fetchJobDetails(true);
      }, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [hasRunningExecution, jobType, currentPage]);

  const fetchJobDetails = async (silent = false) => {
    if (!jobType) return;

    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const [jobResponse, executionsResponse] = await Promise.all([
        APIRoutes.getJob(jobType),
        APIRoutes.listJobExecutions(jobType, {
          limit: pageSize,
          next: cursorToken,
          sort: 'startTime',
          sortDirection: 'DESC'
        })
      ]);

      setJob(jobResponse);
      setExecutions(executionsResponse.executions);
      setTotal(executionsResponse.total);
    } catch (err) {
      if (!silent) {
        setError('Fehler beim Laden der Job-Details');
        handleAPIError(err);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleRunJob = async () => {
    if (!jobType) return;

    try {
      setRunningJob(true);
      await APIRoutes.runJob(jobType);
      showSuccessToast(`Job ${jobType} wurde erfolgreich gestartet`);

      // Refresh the job details to get updated running status
      await fetchJobDetails();
    } catch (err) {
      handleAPIError(err);
    } finally {
      setRunningJob(false);
    }
  };

  const getStatusIcon = (status: JobStatus) => {
    if (status === 'RUNNING') {
      return <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-yellow-500 animate-spin" />;
    } else if (status === 'SUCCESS') {
      return <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500" />;
    } else if (status === 'FAILED') {
      return <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  const getStatusBadge = (status: JobStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    switch (status) {
      case 'RUNNING':
        return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300`;
      case 'SUCCESS':
        return `${baseClasses} bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300`;
      case 'FAILED':
        return `${baseClasses} bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300`;
      default:
        return `${baseClasses} bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-300`;
    }
  };

  const formatDuration = (startTime: string, endTime: string | null) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diff = end.getTime() - start.getTime();

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatNextRun = (nextRun: string) => {
    const date = new Date(nextRun);
    const now = new Date();
    const diff = date.getTime() - now.getTime();

    if (diff < 0) {
      return 'Überfällig';
    }

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `in ${days} Tag${days > 1 ? 'en' : ''}`;
    } else if (hours > 0) {
      return `in ${hours} Stunde${hours > 1 ? 'n' : ''}`;
    } else if (minutes > 0) {
      return `in ${minutes} Minute${minutes > 1 ? 'n' : ''}`;
    } else {
      return 'Läuft bald';
    }
  };

  const getLogCountBadge = (execution: JobExecution) => {
    const total = execution.logCount;
    const errors = execution.logCountError + execution.logCountFatal;
    const warnings = execution.logCountWarn;

    if (errors > 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300">
          {total} ({errors} Fehler)
        </span>
      );
    } else if (warnings > 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300">
          {total} ({warnings} Warnungen)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-300">
          {total}
        </span>
      );
    }
  };

  if (!jobType) {
    return (
      <>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400">Ungültiger Job-Typ</p>
          </div>
        </div>
      </>
    );
  }

  function isNoOpExecution(e: JobExecution) {
    if (e.changes > 0) return false;
    if (e.logCountError > 0) return false;
    if (e.logCountFatal > 0) return false;
    if (e.status === "RUNNING") return false;
    return e.status !== "FAILED";
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/jobs"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {job?.name || jobType}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Job-Details und Ausführungshistorie
                {hasRunningExecution && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300">
                    <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 animate-spin mr-1" />
                    Auto-Refresh aktiv
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleRunJob}
            disabled={job?.running || runningJob}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {job?.running || runningJob ? (
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <FontAwesomeIcon icon={faPlay} className="w-4 h-4 mr-2" />
            )}
            {job?.running || runningJob ? 'Läuft...' : 'Jetzt starten'}
          </button>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && !error && job && (
          <>
            {/* Job Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Job-Informationen
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                    <dd className="mt-1 flex items-center">
                      {job.running ? (
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-yellow-500 animate-spin mr-2" />
                      ) : (
                        <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500 mr-2" />
                      )}
                      <span className="text-sm text-gray-900 dark:text-white">
                        {job.running ? 'Läuft' : 'Bereit'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Zeitplan</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{job.timing}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Nächster Lauf</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{job.next ? formatNextRun(job.next) : 'Nicht geplant'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Timeout</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{job.timeout}s</dd>
                  </div>
                </div>
              </div>
            </div>

            {/* Execution History */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Ausführungshistorie ({total})
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Startzeit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Dauer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Änderungen
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Logs
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Fehler
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {executions.map((execution) => (
                      <tr key={execution.id} className={"hover:bg-gray-50 dark:hover:bg-gray-700" + (isNoOpExecution(execution) ? " opacity-30" : "")}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(execution.status)}
                            <span className={getStatusBadge(execution.status)}>
                              {execution.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDate(execution.startTime, true)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDuration(execution.startTime, execution.endTime)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {execution.changes}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getLogCountBadge(execution)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {execution.error && (
                            <div className="max-w-xs truncate" title={execution.error}>
                              <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-500 mr-1" />
                              {execution.error}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {(execution.logCount > 0 || execution.status === 'RUNNING') && (
                            <Link
                              to={`/admin/jobs/${jobType}/executions/${execution.id}/logs`}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <FontAwesomeIcon icon={faFileText} className="w-4 h-4 mr-1" />
                              Logs
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {executions.length === 0 && (
                <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  Keine Ausführungen gefunden.
                </div>
              )}

              {executions.length > 0 && (
                <PageBasedPagination
                  currentPage={currentPage}
                  totalCount={total}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  loading={loading}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
